import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, Stack, Typography,
} from '@mui/material';
import IosShareIcon from '@mui/icons-material/IosShare';
import brand from './brand';

/**
 * The offer to install, made once, wherever the browser can actually act on it.
 *
 * A gate or parking clerk works from a phone all day, and a browser tab is the wrong shape for
 * that: the address bar eats a line of the screen and the app sits behind whatever else is open.
 * Installed, it opens from the home screen at full height. The same is true of a desk: Chrome and
 * Edge install it into its own window with its own taskbar icon, so a yard desk open all shift is
 * not one tab among thirty. It was once offered only to a coarse pointer, which kept it off every
 * desktop for no reason beyond the assumption that installing is a phone thing.
 *
 * Nothing is offered where the browser cannot do it: the button appears only once the browser has
 * said the app is installable, and the iOS instruction only on Safari, the one iOS browser Apple
 * lets install anything.
 *
 * The offer is one line. It once explained what installing gains you, including a promise about
 * "the challan camera one tap away" that installing does nothing for; nobody needs the pitch, they
 * need to know what the button does. On iOS the instruction stays, because there is no button.
 *
 * The browser fires beforeinstallprompt when it considers the app installable — before React has
 * mounted, in most loads. So the event is caught at module scope, kept, and handed to the component
 * when it mounts; waiting until an effect runs loses the only chance to offer the install at all.
 */
const DEFERRED = { event: null };
const AVAILABLE = 'app:install-available';
const DISMISSED = 'app:install-dismissed';
// Declining is an answer, not a postponement to be re-asked on the next screen. It is taken for a
// month, which is long enough that nobody is nagged and short enough that a new phone gets asked.
const SILENCE_MS = 30 * 24 * 60 * 60 * 1000;

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Chrome shows its own bar unless the page takes the event; taking it is what lets the offer be
    // made in the product's own words, at a moment that is not mid-transaction.
    event.preventDefault();
    DEFERRED.event = event;
    window.dispatchEvent(new CustomEvent(AVAILABLE));
  });
  window.addEventListener('appinstalled', () => { DEFERRED.event = null; });
}

// Storage is unavailable in a private window and in some managed browsers, and a thrown quota error
// must not take the app down with it.
const read = (key) => { try { return window.localStorage.getItem(key); } catch { return null; } };
const write = (key, value) => { try { window.localStorage.setItem(key, value); } catch { /* not worth failing over */ } };

const installed = () => window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
// iOS never fires beforeinstallprompt: Safari installs only through its own share sheet, and no
// other iOS browser can install at all. So it is offered as the instruction it actually is.
const iosSafari = () => /iPad|iPhone|iPod/.test(window.navigator.userAgent)
  && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(window.navigator.userAgent);

const InstallPrompt = () => {
  const [prompt, setPrompt] = useState(null);

  useEffect(() => {
    if (installed()) return undefined;
    const recent = Number(read(DISMISSED));
    if (recent && Date.now() - recent < SILENCE_MS) return undefined;

    // A few seconds in: long enough that it does not land on top of a page still drawing itself,
    // short enough to be the same visit the person opened the link in.
    let timer = window.setTimeout(() => {
      if (DEFERRED.event) setPrompt('install');
      else if (iosSafari()) setPrompt('ios');
    }, 4000);
    const onAvailable = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setPrompt('install'), 4000);
    };
    window.addEventListener(AVAILABLE, onAvailable);
    return () => { window.clearTimeout(timer); window.removeEventListener(AVAILABLE, onAvailable); };
  }, []);

  const close = () => { write(DISMISSED, String(Date.now())); setPrompt(null); };

  const install = async () => {
    const { event } = DEFERRED;
    setPrompt(null);
    if (!event) return;
    DEFERRED.event = null;
    event.prompt();
    // Declining the browser's own dialog is also a decline of ours, so it is not offered again
    // straight away; accepting needs no record, because an installed app never asks.
    const choice = await event.userChoice.catch(() => null);
    if (choice?.outcome !== 'accepted') write(DISMISSED, String(Date.now()));
  };

  return (
    <Dialog open={!!prompt} onClose={close} fullWidth maxWidth="xs">
      <DialogContent>
        <Stack direction="row" spacing={2} alignItems="center">
          {/* This product's own mark, not the one the shared build happens to carry. The offer to
              install is the one place the icon is shown before the system has copied it anywhere,
              and showing another product's logo here is how somebody comes to install what looks
              like the wrong application. The address says which product this is; brand.js has
              said so since the page loaded. Null where a product has no mark of its own, and the
              build's logo is then its logo. */}
          <Box component="img" src={brand.installIcon || '/logo192.png'} alt="" sx={{ width: 56, height: 56, borderRadius: 2, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6">Install this as an app</Typography>
          </Box>
        </Stack>
        {prompt === 'ios' && (
          <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 2 }}>
            {'Tap '}
            <IosShareIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom' }} />
            {' at the bottom of Safari, then '}
            <Box component="span" sx={{ fontWeight: 600 }}>Add to Home Screen</Box>
            .
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Not now</Button>
        {prompt === 'install' && <Button variant="contained" onClick={install}>Install</Button>}
      </DialogActions>
    </Dialog>
  );
};

export default InstallPrompt;
